import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App.jsx";
import { ThemeProvider } from "./contexts/ThemeContext.tsx";
import { UserRoleProvider } from "./contexts/UserRoleContext";
import { AuthProvider } from "./contexts/AuthContext";
import { BrowserRouter } from "react-router-dom";
import { configureAxios } from "./services/api";
import { initBundleMonitor } from "./utils/bundleMonitor";
import { initPerformanceMonitor } from "./utils/performanceMonitor";
import performanceOptimizer from "./services/performanceOptimizer.js";
import performanceService from "./services/performanceService.js";
import { initSentry } from "./config/sentry";
import { initializeHostedAnalytics } from "./utils/hostedAnalytics";
import { installToastDedupe } from "./utils/toastDedupe";

installToastDedupe();

// Initialize Sentry error tracking
initSentry();

// Configure axios — adds camelCase↔snake_case converters + performance interceptors
configureAxios();

const disposeBundleMonitor =
  import.meta.env.MODE === "development"
    ? initBundleMonitor()
    : () => undefined;
const disposePerformanceMonitor = initPerformanceMonitor(
  import.meta.env.MODE === "development"
    ? { debugMode: true }
    : { debugMode: false, sampleRate: 0.1 },
);
const disposeHostedAnalytics = initializeHostedAnalytics();
const optimizerEnabled = import.meta.env.MODE !== "development";
if (optimizerEnabled) {
  performanceOptimizer.init();
}

// Prefetch likely next routes after initial render
let performanceInfrastructureActive = true;
const routePrefetchTimeout = window.setTimeout(() => {
  if (!performanceInfrastructureActive) return;

  const path = window.location.pathname;
  if (path === "/login" || path === "/register") {
    import("./pages/auth/Login.jsx");
    import("./pages/auth/Register");
  } else if (path.startsWith("/dashboard")) {
    import("./pages/Dashboard");
  } else if (path.startsWith("/contacts")) {
    import("./pages/Contacts");
  }
}, 2000);

const disposePerformanceInfrastructure = () => {
  if (!performanceInfrastructureActive) return;
  performanceInfrastructureActive = false;

  window.clearTimeout(routePrefetchTimeout);
  window.removeEventListener("pagehide", disposePerformanceInfrastructure);
  disposeHostedAnalytics();
  disposeBundleMonitor();
  performanceService.cleanup();
  disposePerformanceMonitor();
  if (optimizerEnabled) {
    performanceOptimizer.cleanup();
  }
};

window.addEventListener("pagehide", disposePerformanceInfrastructure);
if (import.meta.hot) {
  import.meta.hot.dispose(disposePerformanceInfrastructure);
}

const RootApp = (
  <BrowserRouter>
    <AuthProvider>
      <UserRoleProvider>
        <ThemeProvider>
          <App />
        </ThemeProvider>
      </UserRoleProvider>
    </AuthProvider>
  </BrowserRouter>
);

const isProduction = import.meta.env.PROD;

createRoot(document.getElementById("root")).render(
  isProduction ? RootApp : <StrictMode>{RootApp}</StrictMode>,
);
